import { TestMarke } from "@/components/TestMarke";
import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, UserPlus, ArrowUp, ArrowDown, Plus, PenLine } from "lucide-react";
import { KundeQuickActions } from "@/components/KundeQuickActions";
import { KundenFormModal } from "@/pages/KundenModulPage";
import { api } from "@/lib/api";

// Suchtext → Vor-/Nachname („Bernd Meier“ → Bernd / Meier, ein Wort → Nachname)
const ausSuchtext = (t = "") => {
  const teile = t.trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return {};
  if (teile.length === 1) return { nachname: teile[0] };
  return { vorname: teile.slice(0, -1).join(" "), nachname: teile[teile.length - 1] };
};

// A-020: Anschrift frei gestaltet = Adresse mit Zeilenumbrüchen (aus dem Beleg oder der Briefanschrift des Kunden)
const istFrei = (c) => !!c && (c.anschrift_frei || (typeof c.address === "string" && c.address.includes("\n")));
const anschriftZeilen = (c) => {
  if (!c) return [];
  if (istFrei(c)) return [c.name, ...(c.address || "").split("\n")].map((z) => (z || "").trim()).filter(Boolean);
  const name = (c.vorname || c.nachname) ? `${c.vorname || ""} ${c.nachname || ""}`.trim() : c.name;
  return [c.firma, name, ...(c.address || "").split(/,\s*/)].map((z) => (z || "").trim()).filter(Boolean);
};

// Fenster „Anschrift anpassen“: Zeilen ändern, einfügen, verschieben, löschen (Ralph 22.10.: Angebot an die Stadtverwaltung mit Abteilungen)
const AnschriftFenster = ({ zeilen: start, onClose, onUebernehmen, onStandard }) => {
  const [zeilen, setZeilen] = useState(start.length ? start : [""]);
  const setze = (i, v) => setZeilen((z) => z.map((x, j) => (j === i ? v : x)));
  const schiebe = (i, d) => setZeilen((z) => { const n = [...z]; const k = i + d; if (k < 0 || k >= n.length) return z; [n[i], n[k]] = [n[k], n[i]]; return n; });
  const weg = (i) => setZeilen((z) => (z.length > 1 ? z.filter((_, j) => j !== i) : z));
  const dazu = (i) => setZeilen((z) => [...z.slice(0, i + 1), "", ...z.slice(i + 1)]);
  const fertig = zeilen.map((z) => z.trim()).filter(Boolean);
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="anschrift-fenster">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Anschrift auf diesem Beleg</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" aria-label="Schließen"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground">Genau so steht sie im Brieffenster und im PDF. Erste Zeile fett. Höchstens 6 Zeilen passen ins Fenster.</p>
        <div className="space-y-1.5">
          {zeilen.map((z, i) => (
            <div key={i} className="flex items-center gap-1">
              <input value={z} onChange={(e) => setze(i, e.target.value)} className={`flex-1 h-9 rounded-sm border border-input bg-background px-2 text-sm ${i === 0 ? "font-semibold" : ""}`}
                placeholder={i === 0 ? "Empfänger (z. B. Freie und Hansestadt Hamburg)" : "Zeile (z. B. Abteilung, Straße, PLZ Ort)"} data-testid={`anschrift-zeile-${i}`} />
              <button onClick={() => schiebe(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-muted disabled:opacity-30" title="nach oben" data-testid={`anschrift-hoch-${i}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => schiebe(i, 1)} disabled={i === zeilen.length - 1} className="p-1.5 rounded hover:bg-muted disabled:opacity-30" title="nach unten" data-testid={`anschrift-runter-${i}`}><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => dazu(i)} className="p-1.5 rounded hover:bg-muted" title="Zeile darunter einfügen" data-testid={`anschrift-dazu-${i}`}><Plus className="w-4 h-4" /></button>
              <button onClick={() => weg(i)} disabled={zeilen.length === 1} className="p-1.5 rounded hover:bg-muted disabled:opacity-30" title="Zeile löschen" data-testid={`anschrift-weg-${i}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {fertig.length > 6 && <p className="text-xs text-amber-700">Mehr als 6 Zeilen: das Brieffenster im Umschlag zeigt evtl. nicht alles.</p>}
        <p className="text-xs text-muted-foreground">Gilt nur für diesen Beleg. Dauerhafte Zusatzzeilen (z. B. Abteilung) tragen Sie beim Kunden unter Adresse ein („+ Zeile“).</p>
        <div className="flex justify-end gap-2 flex-wrap">
          <button onClick={onStandard} className="mr-auto px-3 py-1.5 text-sm text-muted-foreground hover:underline" title="Anschrift wieder aus den Kundendaten (mit den Zusatzzeilen des Kunden)" data-testid="anschrift-standard">Standard aus Kundendaten</button>
          <button onClick={onClose} className="px-3 py-1.5 border rounded-md text-sm hover:bg-muted">Abbrechen</button>
          <button onClick={() => onUebernehmen(fertig)} disabled={!fertig.length} className="px-3 py-1.5 rounded-md text-sm bg-primary text-primary-foreground disabled:opacity-50" data-testid="anschrift-uebernehmen">Übernehmen</button>
        </div>
      </div>
    </div>
  );
};

const DocumentHeader = ({
  settings, customer, customers, selectedCustomerId,
  handleCustomerChange, onKundeAngelegt, onAnschrift, neueAnschrift, onNeueAnschrift, type, docNumber, abNummer, createdAt,
  setPositions, positions,
}) => {
  const [kundeSearch, setKundeSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [neuAnlegen, setNeuAnlegen] = useState(false);  // Ralph 22.10.: Kunde direkt im Beleg anlegen
  const [bearbeiten, setBearbeiten] = useState(null);  // A-020: geladener Kunde für „Kunde bearbeiten“
  const [anschriftOffen, setAnschriftOffen] = useState(false);  // A-020: Anschrift frei gestalten
  const kundeBearbeiten = async () => {
    try { const r = await api.get(`/modules/kunden/data/${customer.id || selectedCustomerId}`); setBearbeiten(r.data); } catch { /* bleibt zu */ }
  };
  const uebernehmen = async (kundeId) => {
    setNeuAnlegen(false);
    setKundeSearch("");
    if (!kundeId) return;
    try { const r = await api.get(`/modules/kunden/data/${kundeId}`); onKundeAngelegt(r.data); } catch { /* Liste lädt beim nächsten Öffnen */ }
  };

  const suggestions = (() => {
    const term = kundeSearch.toLowerCase().trim();
    if (!term) return customers;
    return customers.filter(c => {
      const vorname = (c.vorname || "").toLowerCase();
      const nachname = (c.nachname || "").toLowerCase();
      const name = (c.name || "").toLowerCase();
      const firma = (c.firma || "").toLowerCase();
      return vorname.includes(term) || nachname.includes(term) || name.includes(term) || firma.includes(term);
    });
  })();

  return (
    <div className="p-4 lg:p-10 border-b">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        {/* Left: Briefkopf + Brieffenster */}
        <div>
          <div className="flex items-baseline gap-0.5 mb-0.5">
            <span className="text-2xl lg:text-4xl font-bold tracking-tight" style={{ color: "#0F2A4E" }}>Tischlerei</span>
            <span className="text-2xl lg:text-4xl font-bold tracking-tight" style={{ color: "#003399" }}>Graupner</span>
            <span className="text-xs lg:text-sm font-semibold ml-1.5" style={{ color: "#CC0000" }}>seit 1960</span>
          </div>
          <p className="text-xs lg:text-sm font-medium tracking-wide mb-3" style={{ color: "#003399" }}>Mitglied der Handwerkskammer Hamburg</p>
          {/* Abstand für Brieffenster-Höhe */}
          <div className="h-6 lg:h-8"></div>
          {/* DIN 5008 Brieffenster */}
          <div className="max-w-sm">
            <p className="text-[9px] lg:text-[10px] text-slate-600 border-b border-slate-300 pb-0.5 mb-2 tracking-wide">
              Tischlerei Graupner · Erlengrund 129 · 22453 Hamburg
            </p>
            {!customer ? (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                  <input
                    value={kundeSearch}
                    onChange={e => { setKundeSearch(e.target.value); setShowDropdown(true); }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Kunde suchen (Nachname, Firma, E-Mail)..."
                    className="w-full h-10 rounded-sm border border-input bg-white pl-9 pr-3 text-sm"
                    data-testid="wysiwyg-customer-search"
                  />
                </div>
                {showDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded-sm shadow-lg max-h-48 overflow-y-auto">
                    {onKundeAngelegt && (
                      <button onClick={() => { setShowDropdown(false); setNeuAnlegen(true); }}
                        className="w-full text-left px-3 py-2 text-sm font-medium text-primary hover:bg-muted border-b flex items-center gap-2"
                        data-testid="wysiwyg-kunde-neu">
                        <UserPlus className="w-4 h-4" /> Neuen Kunden anlegen{kundeSearch.trim() ? ` „${kundeSearch.trim()}“` : ""}
                      </button>
                    )}
                    {suggestions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-600">Keine Kunden gefunden</div>
                    ) : suggestions.map(c => {
                      const displayName = (c.vorname || c.nachname) ? `${c.nachname || ""}, ${c.vorname || ""}`.trim().replace(/^,\s*/, "") : (c.name || "");
                      const status = c.status || c.kontakt_status || "";
                      return (
                        <button key={c.id} onClick={() => { handleCustomerChange(c.id); setKundeSearch(""); setShowDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b last:border-0 flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{displayName}</span>
                            {c.testkunde === true && <TestMarke className="ml-1.5" />}
                            {c.firma && <span className="text-slate-600 ml-1">({c.firma})</span>}
                            {c.email && <span className="text-xs text-slate-600 block truncate">{c.email}</span>}
                          </div>
                          {status && <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded flex-shrink-0">{status}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="min-h-[70px]">
                <div className="flex items-start justify-between">
                  <KundeQuickActions customerId={customer.id || selectedCustomerId} testId="editor-customer-quickactions"
                    onBearbeiten={onKundeAngelegt ? kundeBearbeiten : null}>
                    {istFrei(customer) ? (
                      // A-020: frei gestaltete Anschrift (Zeilen genau so wie auf dem Beleg)
                      <div className="text-sm leading-relaxed" data-testid="editor-anschrift-frei">
                        <p className="font-semibold text-slate-900">{customer.name}</p>
                        <p className="whitespace-pre-line text-slate-800">{customer.address}</p>
                      </div>
                    ) : (
                      <div className="text-sm leading-relaxed">
                        <p className="font-semibold text-slate-900">{(customer.vorname || customer.nachname) ? `${customer.vorname || ""} ${customer.nachname || ""}`.trim() : customer.name}</p>
                        {customer.firma && <p className="text-slate-600">{customer.firma}</p>}
                        {customer.address && (
                          <p className="whitespace-pre-line text-slate-800">{customer.address.split(/,\s*/).join("\n")}</p>
                        )}
                      </div>
                    )}
                  </KundeQuickActions>
                  <button
                    onClick={() => { handleCustomerChange(""); }}
                    className="text-xs text-slate-500 hover:text-slate-800 ml-2 mt-0.5"
                    title="Anderen Kunden wählen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {neueAnschrift && (  /* A-020 P2b: Kundendaten seit dem Speichern geändert */
                  <div className="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex items-center gap-2 flex-wrap" data-testid="neue-anschrift-hinweis">
                    Anschrift beim Kunden geändert: {[neueAnschrift.name, ...String(neueAnschrift.address || "").split(/\n|,\s*/)].filter(Boolean).join(" · ")}
                    <button onClick={onNeueAnschrift} className="underline font-medium" data-testid="neue-anschrift-uebernehmen">Neue Anschrift übernehmen</button>
                  </div>
                )}
                {onAnschrift && (
                  <button onClick={() => setAnschriftOffen(true)} className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="editor-anschrift-anpassen">
                    <PenLine className="w-3.5 h-3.5" /> Anschrift anpassen (Zeilen, Abteilung …)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Right: Adresse in Blau */}
        <div className="sm:text-right text-xs lg:text-sm font-medium" style={{ color: "#003399" }}>
          <p className="font-bold">Tischlerei Graupner</p>
          <p>Erlengrund 129</p>
          <p>22453 Hamburg</p>
          <p>Tel.: 040 55567744</p>
          <p>Service24@tischlerei-graupner.de</p>
          <p>www.tischlerei-graupner.de</p>
          <p>Steuernummer: 45/076/04744</p>
          {/* Dokument-Metadaten */}
          <div className="mt-3 space-y-0.5 text-xs lg:text-sm">
            <p>Kd.-Nr.: {selectedCustomerId ? selectedCustomerId.substring(0, 8).toUpperCase() : "-"}</p>
            {type === "invoice" && abNummer && <p data-testid="editor-ab-nummer">Auft.-Nummer: {abNummer}</p>}
            <p>Datum: {new Date(createdAt).toLocaleDateString("de-DE")}</p>
            <p>{type === "quote" ? "Angebots-Nr." : type === "order" ? "Auftrags-Nr." : "Rechnungs-Nr."}: {docNumber || (type === "invoice" ? (customer?.testkunde === true ? "Übungsbeleg" : "Entwurf") : "-")}</p>
          </div>
        </div>
      </div>
      {bearbeiten && createPortal(
        <KundenFormModal
          isOpen={true}
          kunde={bearbeiten}
          popoutEnabled={false}
          onClose={() => setBearbeiten(null)}
          onSave={() => { const id = bearbeiten.id; setBearbeiten(null); uebernehmen(id); }}
        />,
        document.body
      )}
      {anschriftOffen && createPortal(
        <AnschriftFenster
          zeilen={anschriftZeilen(customer)}
          onClose={() => setAnschriftOffen(false)}
          onUebernehmen={(zeilen) => { setAnschriftOffen(false); onAnschrift(zeilen); }}
          onStandard={() => { setAnschriftOffen(false); onAnschrift(null, false); }}
        />,
        document.body
      )}
      {/* dasselbe Fenster „Neuer Kunde“ wie unter Kunden (inkl. Doppelte-Prüfung und Frage „Projekt erstellen?“);
          per Portal an <body>, weil das Briefblatt verschoben/skaliert ist und das Fenster sonst seitlich herausragt */}
      {neuAnlegen && createPortal(
        <KundenFormModal
          isOpen={true}
          kunde={null}
          popoutEnabled={false}
          vorbelegung={ausSuchtext(kundeSearch)}
          onClose={() => setNeuAnlegen(false)}
          onSave={(kundeId) => uebernehmen(kundeId)}
          onVorhandenNehmen={(kundeId) => uebernehmen(kundeId)}
        />,
        document.body
      )}
    </div>
  );
};

export { DocumentHeader };
